// # Mentor Dashboard Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import './mentor.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';

// # Import icons
import calendarIcon from '../../../aneo_media/mentor/calendar.png';
import meetingIcon from '../../../aneo_media/mentor/meeting.png';
import menteesIcon from '../../../aneo_media/mentor/mentees.png';
import workspaceIcon from '../../../aneo_media/mentor/workspace.png';

const MentorDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Add admin access check logging
  React.useEffect(() => {
    const startTime = performance.now();
    
    console.log('Mentor Dashboard - Access Check:', {
      operation: 'mentor_access_check',
      hasProfile: !!userProfile,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });
  }, [userProfile]);

  // # Navigation options with updated paths
  const navigationCards = [
    {
      title: 'Calendar',
      icon: calendarIcon,
      path: '/admin/mentorship/calendar',
      description: 'Manage your mentorship schedule'
    },
    {
      title: 'Meetings',
      icon: meetingIcon,
      path: '/admin/mentorship/meetings',
      description: 'Organize and track mentoring sessions'
    },
    {
      title: 'Mentees',
      icon: menteesIcon,
      path: '/admin/mentorship/mentees',
      description: 'View and manage your mentees'
    },
    {
      title: 'My Space',
      icon: workspaceIcon,
      path: '/admin/mentorship/study',
      description: 'Admin Personal Space'
    }
  ];

  // # Use direct navigation instead of relative
  const handleNavigation = (path) => {
    const startTime = performance.now();
    
    console.log('Mentor Dashboard - Navigation:', {
      operation: 'mentor_navigation',
      path: path,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });
    
    navigate(path);
  };

  return (
    <div className={`mntr_mentor-container ${isDarkMode ? 'mntr_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mntr_mentor-main-content">
        {/* # Navigation Cards Grid */}
        <div className="mntr_navigation-grid">
          {navigationCards.map((card) => (
            <div 
              key={card.title} 
              className="mntr_nav-card"
              onClick={() => handleNavigation(card.path)}
            >
              <img src={card.icon} alt={card.title} className="mntr_nav-icon" />
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default MentorDashboard;
